import React from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Row, Col, ListGroup, ListGroupItem, Badge, Button, Input } from "reactstrap";
import moment from "moment";
import { ClipLoader } from "react-spinners";

function CommentsModal({
  isOpen,
  onClose,
  timeline = [],
  totals = { total: 0, participants: 0, averagePerPeriod: 0, periods: 0 },
  comments = [],
  usersCompany = [],
  onOpenUserDetail,
  onOpenComment,
  onNavigateToClassroom,
  navigatingCommentId,
  onExport,
  topClassrooms = [],
  topUsers = [],
  formatTimelineLabel
}) {
  const [searchValue, setSearchValue] = React.useState("");
  const averagePerPeriod = totals.averagePerPeriod ? Number(totals.averagePerPeriod).toFixed(1) : "0.0";

  const filteredComments = Array.isArray(comments) ? comments.filter(comment => {
    const term = (searchValue || "").trim().toLowerCase();
    if (!term) return true;
    const user = comment.user || {};
    const decoratedUser = usersCompany.find(u => String(u.id) === String(user.id)) || {};
    const fields = [
      user.name,
      user.lastName,
      user.email,
      decoratedUser.name,
      decoratedUser.lastName,
      decoratedUser.email,
      comment.comment,
      comment.classroomName
    ];
    return fields
      .filter(Boolean)
      .map(value => value.toString().toLowerCase())
      .some(value => value.includes(term));
  }) : [];

  const hasCommentsToExport = filteredComments.length > 0;

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Comentários · visão completa</ModalHeader>
      <ModalBody>
        <Row className="mb-4 text-center">
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Comentários totais</div>
            <div className="h3 font-weight-bold mb-0">{totals.total}</div>
          </Col>
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Participantes</div>
            <div className="h3 font-weight-bold mb-0">{totals.participants}</div>
          </Col>
          <Col md="4" sm="6" className="mb-3">
            <div className="text-sm text-muted">Média por período</div>
            <div className="h3 font-weight-bold mb-0">{averagePerPeriod}</div>
          </Col>
        </Row>

        <h6 className="text-uppercase text-muted ls-1 mb-2">Histórico completo</h6>
        <ListGroup flush>
          {Array.isArray(timeline) && timeline.length > 0 ? (
            timeline.map((item, index) => {
              const total = Number(item?.total ?? item?.count ?? item?.value ?? 0) || 0;
              const participants = Number(item?.participants ?? item?.users ?? 0) || 0;
              const label = typeof formatTimelineLabel === 'function' ? formatTimelineLabel(item) : (item.label || "Período");
              return (
                <ListGroupItem key={`${label}-${index}`} className="d-flex justify-content-between align-items-center py-2">
                  <div>
                    <div className="font-weight-bold">{label}</div>
                    <div className="small text-muted">{total} comentários</div>
                  </div>
                  {participants > 0 ? <Badge color="info" pill>{participants} participantes</Badge> : null}
                </ListGroupItem>
              );
            })
          ) : (
            <ListGroupItem className="py-3 d-flex justify-content-between align-items-center">
              <div>
                <div className="font-weight-bold">Resumo consolidado</div>
                <div className="small text-muted">Todos os períodos registrados</div>
              </div>
              <div className="text-sm text-right">
                <span className="text-primary"><i className="fas fa-comments mr-1"></i>{totals.total} comentários</span>
              </div>
            </ListGroupItem>
          )}
        </ListGroup>

        <h6 className="text-uppercase text-muted ls-1 mt-4 mb-2">Todos os comentários</h6>
        {Array.isArray(comments) && comments.length > 0 ? (
          <>
            <div className="mb-3">
              <Input type="search" value={searchValue} onChange={e => setSearchValue(e.target.value)} placeholder="Pesquisar por nome, e-mail ou conteúdo" />
            </div>
            {filteredComments.length > 0 ? (
              <ListGroup flush className="mb-3">
                {filteredComments.map(comment => {
                  const user = comment.user || {};
                  const decoratedUser = usersCompany.find(u => String(u.id) === String(user.id)) || {};
                  const avatar = user.photo || decoratedUser.photo || "/images/defaultAvatar.png";
                  const nameParts = [user.name || decoratedUser.name, user.lastName || decoratedUser.lastName].filter(Boolean);
                  const displayName = nameParts.length > 0 ? nameParts.join(" ") : "Usuário";
                  const createdAt = comment.createDate ? moment(comment.createDate).format("DD/MM/YYYY HH:mm") : "Sem data";
                  const detailData = {
                    ...decoratedUser,
                    id: decoratedUser.id || user.id,
                    name: decoratedUser.name || user.name,
                    lastName: decoratedUser.lastName || user.lastName,
                    email: decoratedUser.email || user.email,
                    photo: avatar,
                    accessLevel: decoratedUser.accessLevel || user.accessLevel,
                    blocked: decoratedUser.blocked ?? user.blocked
                  };
                  return (
                    <ListGroupItem key={`comment-full-${comment.id}`} className="py-3">
                      <div className="d-flex">
                        <div className="mr-3" style={{ width: "48px", height: "48px", borderRadius: "50%", overflow: "hidden" }}>
                          <img src={avatar} alt={displayName} className="rounded-circle" style={{ width: "48px", height: "48px", objectFit: "cover" }} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <div className="font-weight-bold">{displayName}</div>
                              <div className="small text-muted">{createdAt}</div>
                            </div>
                            <div className="d-flex align-items-center">
                              {comment.classroomName ? (
                                <Badge color="info" pill className="mr-2">{comment.classroomName}</Badge>
                              ) : (
                                <Badge color="secondary" pill className="mr-2">Comentário geral</Badge>
                              )}
                              <Button size="sm" color="link" className="mr-2" onClick={() => onOpenUserDetail && onOpenUserDetail(detailData)}>Usuário</Button>
                              <Button size="sm" color="primary" outline className="ml-2" onClick={() => onOpenComment && onOpenComment(comment)}>
                                Ver comentário
                              </Button>
                              <Button size="sm" color="primary" className="ml-2" onClick={() => onNavigateToClassroom && onNavigateToClassroom(comment)}>
                                {navigatingCommentId === comment.id ? <ClipLoader color="#fff" size={16} /> : "Ir para a sala"}
                              </Button>
                            </div>
                          </div>
                          <div className="mt-2 text-sm text-muted" style={{ whiteSpace: "pre-line" }}>
                            {comment.comment || "Sem conteúdo."}
                          </div>
                        </div>
                      </div>
                    </ListGroupItem>
                  );
                })}
              </ListGroup>
            ) : (
              <div className="text-center text-muted py-4">Nenhum comentário corresponde à pesquisa.</div>
            )}
          </>
        ) : (
          <div className="text-center text-muted py-4">Nenhum comentário encontrado para esta unidade.</div>
        )}

        {(Array.isArray(topClassrooms) && topClassrooms.length > 0) || (Array.isArray(topUsers) && topUsers.length > 0) ? (
          <Row className="mt-4">
            {Array.isArray(topClassrooms) && topClassrooms.length > 0 ? (
              <Col md="6" className="mb-3">
                <h6 className="text-uppercase text-muted ls-1 mb-2">Turmas mais ativas</h6>
                <ListGroup flush>
                  {topClassrooms.map((item, index) => {
                    const total = Number(item?.total ?? item?.count ?? item?.comments ?? 0) || 0;
                    const participants = Number(item?.participants ?? item?.users ?? 0) || 0;
                    return (
                      <ListGroupItem key={`${item?.id || index}-classroom`} className="py-2 d-flex justify-content-between align-items-center">
                        <div>
                          <div className="font-weight-bold text-truncate" style={{ maxWidth: "220px" }}>{item?.name || item?.classroomName || `Turma ${index + 1}`}</div>
                          <div className="small text-muted">{total} comentários</div>
                        </div>
                        {participants > 0 ? <Badge color="primary" pill>{participants} participantes</Badge> : null}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </Col>
            ) : null}
            {Array.isArray(topUsers) && topUsers.length > 0 ? (
              <Col md="6" className="mb-3">
                <h6 className="text-uppercase text-muted ls-1 mb-2">Participantes em destaque</h6>
                <ListGroup flush>
                  {topUsers.map((user, index) => {
                    const total = Number(user?.total ?? user?.count ?? user?.comments ?? 0) || 0;
                    return (
                      <ListGroupItem key={`${user?.id || index}-user`} className="py-2 d-flex justify-content-between align-items-center">
                        <div>
                          <div className="font-weight-bold">{user?.name || user?.userName || `Usuário ${index + 1}`}</div>
                          <div className="small text-muted">{total} comentários</div>
                        </div>
                        {user?.email ? <div className="small text-muted text-right" style={{ maxWidth: "180px" }}>{user.email}</div> : null}
                      </ListGroupItem>
                    );
                  })}
                </ListGroup>
              </Col>
            ) : null}
          </Row>
        ) : null}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <Button color="primary" onClick={() => onExport && onExport(searchValue)} disabled={!hasCommentsToExport}>Exportar CSV</Button>
        <Button color="secondary" onClick={onClose}>Fechar</Button>
      </ModalFooter>
    </Modal>
  );
}

export default CommentsModal;
